
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React from "react";

import { Heading } from "@/components/Heading";
import { Image } from "@/components/Image";
import { Grid } from "@/components/Grid";
import { BlogText } from "@/components/BlogText";
import { CTASection } from "@/sections/CTASection";
import { Head } from "@/components/Head";
import { useTheme } from "@/hooks/useTheme";

const OpenXdFilePrinciple = () => {
  const { colors } = useTheme();
  return (
    <>
      <Head
        pageNamespace="page/open-xd-file-principle"
        ogImage="/images/main-og-image.jpg"
      />

      <div>
        <Grid
          customStyle={{
            maxWidth: 800,
            flexDirection: "column",
            alignItems: "flex-start",
            paddingBottom: "0 !important",
          }}
        >
          <Heading as="h1">Import XD files in Principle</Heading>
          <BlogText>
            Right now, it is not possible to import Adobe XD files into
            Principle, but we have a simple and effective way to do this
            {"\u00A0"}
            <a
              href="/"
              style={{
                color: colors.RED,
                fontWeight: "bold",
              }}
            >
              using our XD converter
            </a>
            .
          </BlogText>
          <BlogText>
            Firstly we need to convert the XD file into a format that Principle
            will accept.
          </BlogText>
          <BlogText>
            Principle accepts both Sketch and Figma files, so we will need to
            convert into one of these formats first.
          </BlogText>
          <BlogText>Let’s get started</BlogText>
          <BlogText>
            Please note: You need to have a working version of Sketch installed
            on your Mac or a Figma account (the Free plan is fine) as Principle
            requires this to do the import of the converted file..
          </BlogText>

          <section>
            <Heading as="h3">Convert your XD File to Sketch or Figma</Heading>
            <ol>
              <li>
                <BlogText>
                  <strong>Upload your file to Magicul</strong>
                  &nbsp;- go to&nbsp;
                  <a
                    href="/"
                    style={{
                      color: colors.RED,
                      fontWeight: "bold",
                    }}
                  >
                    XD to Sketch converter
                  </a>
                  {"\u00A0"}
                  or our
                  {"\u00A0"}
                  <a
                    href="/converter/xd-to-figma"
                    style={{
                      color: colors.RED,
                      fontWeight: "bold",
                    }}
                  >
                    XD to Figma converter
                  </a>
                  {"\u00A0"}
                  and drag a file to the converter or click on browse to upload
                  your file.
                </BlogText>
                <Image
                  customStyle={{ width: "100%", marginBottom: 29 }}
                  src="/images/tutorials/uploading-image.jpg"
                  alt="Magicul uploading dialog"
                />
              </li>
              <li>
                <BlogText>
                  <strong>Select your plan</strong>
                  &nbsp;-&nbsp;Click on convert now and select your plan.
                </BlogText>
              </li>
              <li>
                <BlogText>
                  <strong>Complete the checkout</strong>
                  &nbsp;-&nbsp;enter your details and complete the checkout.
                  Your file will now convert from the XD to Sketch/Figma.
                </BlogText>
              </li>
              <li>
                <BlogText>
                  <strong>Download your file</strong>
                  &nbsp;-&nbsp;click the arrow button to download the converted
                  file.
                </BlogText>
                <Image
                  customStyle={{ width: "100%", marginBottom: 29 }}
                  src="/images/tutorials/downloading-image.jpg"
                  alt="Magicul downloading dialog"
                />
              </li>
            </ol>
            <BlogText>Now let’s import the file into Principle.</BlogText>
          </section>
          <section>
            <Heading as="h2">Importing into Principle</Heading>
            <section>
              <Heading as="h3">The Sketch method</Heading>
              <ol>
                <li>
                  <BlogText>Open the converted file in Sketch</BlogText>
                </li>
                <li>
                  <BlogText>Open Principle</BlogText>
                </li>
                <li>
                  <BlogText>
                    Click on the import button on the left of the screen to
                    start the upload
                  </BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-file-principle/principle-import.jpg"
                    alt="principle import"
                  />
                  <BlogText>
                    Click on
                    {"\u00A0"}
                    <strong>File &gt; import from Sketch</strong>
                  </BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-file-principle/principle-import-sketch.jpg"
                    alt="principle import sketch"
                  />
                </li>
                <li>
                  <BlogText>
                    Principle should detect your open Sketch file. You can now
                    select pages to import from Sketch or select the artboards
                    in Sketch (hold Shift and click on the names of the
                    artboards) first and then you will be able to import the
                    selected artboards.
                  </BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-file-principle/principle-sketch-dialog.jpg"
                    alt="principle sketch dialog"
                  />
                </li>
              </ol>
              <BlogText>
                Now you are ready to enjoy using Principle to create a high
                fidelity animated prototype!
              </BlogText>
              <BlogText>Want to convert XD files to Principle?</BlogText>
              <CTASection
                title="Want to convert XD files to Principle?"
                buttonLabel="Start Now"
                href="/"
              />
            </section>

            <section>
              <Heading as="h3">The Figma method</Heading>
              <ol>
                <li>
                  <BlogText>Open the converted file in Figma</BlogText>
                </li>
                <li>
                  <BlogText>Open Principle</BlogText>
                </li>
                <li>
                  <BlogText>Then go to the main menu and select:</BlogText>
                  <BlogText>
                    Click on
                    {"\u00A0"}
                    <strong>File &gt; Import from Figma</strong>
                  </BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-file-principle/principle-import.jpg"
                    alt="principle import"
                  />
                  <BlogText customStyle={{ textAlign: "center" }}>OR</BlogText>
                  <BlogText>Click on the Import button</BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-file-principle/principle-import-figma.jpg"
                    alt="principle import figma"
                  />
                </li>
                <li>
                  <BlogText>
                    Click on
                    {"\u00A0"}
                    <strong>Connect To Figma</strong>
                  </BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-file-principle/principle-figma-dialog.jpg"
                    alt="principle figma dialog"
                  />
                </li>
                <li>
                  <BlogText>Sign in to Figma</BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-file-principle/figma-signin.jpg"
                    alt="figma signin"
                  />
                </li>
                <li>
                  <BlogText>
                    Click on
                    {"\u00A0"}
                    <strong>Allow access</strong>
                  </BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-file-principle/principle-allow-access.jpg"
                    alt="principle allow access"
                  />
                </li>
                <li>
                  <BlogText>
                    You can now select a file to import into Principle. You can
                    import pages or you can select the Frames in Figma (hold
                    Shift and click on the names of the artboards)
                  </BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-file-principle/principle-figma-dialog-allowed.jpg"
                    alt="principle figma dialog allowed access"
                  />
                </li>
              </ol>
              <BlogText>
                The file will be imported and now you are ready to add all kinds
                of interactions and animations to your artboards.
              </BlogText>
            </section>
            <CTASection
              title="Want to convert and import XD files to Principle?"
              buttonLabel="Start Now"
              href="/"
            />
          </section>
        </Grid>
      </div>
    </>
  );
};

export default OpenXdFilePrinciple;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        
// @ts-ignore
        
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          
// @ts-ignore
          props: {
// @ts-ignore
            
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/tutorials/open-xd-file-principle',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  